import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import logger from "utils/logger";
import Pagination from "components/__new/common/pagination";
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DynamicHeadTag from "components/__new/common/DynamicHeadTag";
import useLoginInfo from "hooks/useLoginInfo";
import processParams from "utils/processParams";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import useUrlQueryParams from "hooks/useUrlQueryParams";
import Maybe from "components/__new/common/Maybe";
import { ButtonLoader } from "components/__new/common/form";
import Table from "components/__new/common/Table";
import Print from "components/__new/common/Print";
import { TableContent } from "components/__new/common/TableContent";
import SearchBox from "components/__new/common/SearchBox";
import DownloadAsCSV from "components/__new/common/DownloadAsCSV";
import * as employeesActions from "store/entities/employees/action";



const List = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const {userInfo} = useLoginInfo();
    const {urlQueryParams} = useUrlQueryParams();
    const { employeesLoading, employees, employeesDownloadLoading, employeesDownload, eraEmployeeLoading } = useSelector((s) => s.entities.employees);
    

    const downloadAsCSVRef = useRef();
    const printComponentRef = useRef();
    const [listParams, setListParams] = useState({
        "page": 1,
        "limit": 20,
    });


    const getEmployees = () => {
        let params = {...listParams};

        params = processParams(urlQueryParams, params, "page", "number", 1, "page");
        params = processParams(urlQueryParams, params, "limit", "number", 20, "limit");
        params = processParams(urlQueryParams, params, "search", "string", "", "fullName");
        params = processParams(urlQueryParams, params, "status", "string", "", "status");

        setListParams(params);        
        dispatch(employeesActions.getAllEmployees(params));
    }


    const generateEraEmployee = (userId, name) => {
        toast("Generating ERA for " + name);
        const params = {
            userId,
            type: "employee",
        }
        dispatch(employeesActions.generateEraEmployee(params));
    }


    const checkEraDetails = (bankName, accountName, accountNumber) => {
        if (bankName === "N/A" || accountName === "N/A" || accountNumber === "N/A"){
            return false;
        }
        return true;
    }


    useEffect(() => {
        getEmployees();
        logger("employer-employee-listings", userInfo);
        // eslint-disable-next-line
    }, [location]);


    useEffect(() => {
        if (eraEmployeeLoading === false){
            getEmployees();
        }
        // eslint-disable-next-line
    }, [eraEmployeeLoading]);


    return ( 
        <div>

            <DynamicHeadTag
                headerText="Employees ERA"
                headerDescription="Manage and monitor your employees' payment activity."
            />


            <div className="lg:flex">
                <div className="w-full lg:w-1/2">
                    <div className="page-title">
                        Employees Reconciliation Account
                    </div>
                </div>
                <div className="w-full lg:w-1/2 flex mt-4 lg:mt-0 lg:justify-end space-x-3"></div>
            </div>

            
            <div className="mt-1">
                <div className="text-sm">
                    An ERA is an account assigned to each employee for their salary transactions. It is the account you pay your employee's salaries into on payday. Whatever they have withdrawn during the month via Earnipay is automatically deducted from their ERAs, and the net balance is transferred into the employee's bank account
                </div>
            </div>


            <div className="mt-6 box">
                <div className="flex flex-wrap justify-between">
                    <div className="max-w-sm flex-grow sm:mr-6">
                        <SearchBox placeholder="Search by name..." name="search" extraUrlParams="page=1" />
                    </div>
                    <div className="flex flex-wrap space-x-3 sm:space-x-6">
                        <div>
                            <DownloadAsCSV
                                ref={downloadAsCSVRef}
                                tooltip="Download employees reconciliation account (ERA) list"
                                filename="Employees Reconciliation Account (ERA).csv"
                                columns={[
                                    { label: "Employee ID", key: "employeeId" },
                                    { label: "Full Name", key: "fullName" },
                                    { label: "Email Address", key: "email" },
                                    { label: "Phone Number", key: "phoneNumber" },
                                    { label: "ERA Bank Name", key: "evaBankName" },
                                    { label: "ERA Account Name", key: "evaAccountName" },
                                    { label: "ERA Account Number", key: "evaAccountNumber" },
                                ]}
                                click={() => {
                                    dispatch(employeesActions.downloadGetAllEmployees({...listParams, page: 1, limit: 10000})).then(() => {
                                        downloadAsCSVRef.current?.downloadData();
                                    });
                                }}
                                loading={employeesDownloadLoading}
                                response={employeesDownload?.employees?.docs}
                            />
                        </div>

                        <div>
                            <Print component={printComponentRef} />
                        </div>
                    </div>
                </div>


                <div className="mt-6">
                    <div ref={printComponentRef}>
                        
                        <div className="mb-4 text-xl hidden print:block">
                            Employees Reconciliation Account
                        </div>

                        <Table>
                            <Table.Head>
                                <th>Employee ID</th>
                                <th>Full Name</th>
                                <th>Phone Number</th>
                                <th>Bank Name</th>
                                <th>Account Name</th>
                                <th>Account Number</th>
                                <th></th>
                            </Table.Head>
                            <Table.Body>

                                <TableContent
                                    loading={employeesLoading}
                                    loadingText={`Loading employees`}
                                    data={employees?.employees}
                                    colspan={7}
                                />

                                {employeesLoading === false && employees?.employees?.docs?.map((employee) =>
                                    <tr key={employee._id}>
                                        <td>
                                            {employee.employeeId}
                                        </td>
                                        <td>
                                            {employee.fullName}
                                        </td>
                                        <td>
                                            {employee.phoneNumber}
                                        </td>
                                        <td>
                                            <div className="font-semibold">
                                                {employee.evaBankName}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="font-semibold">
                                                {employee.evaAccountName}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="font-semibold">
                                                {employee.evaAccountNumber}
                                            </div>
                                        </td>
                                        <td>
                                            <Maybe condition={employee.evaStatus === true && checkEraDetails(employee.evaBankName, employee.evaAccountName, employee.evaAccountNumber) === true}>
                                                <Link to={"/people/employees/era/" + employee._id + "/transactions"} className="btn btn-ep-primary btn-sm">
                                                    View
                                                    <FontAwesomeIcon icon="angle-right" className="ml-2" />
                                                </Link>
                                            </Maybe>

                                            <Maybe condition={employee.evaStatus === false || checkEraDetails(employee.evaBankName, employee.evaAccountName, employee.evaAccountNumber) === false}>
                                                <ButtonLoader type="button" loading={eraEmployeeLoading} onClick={() => generateEraEmployee(employee._id, employee.fullName)} className="btn btn-ep-primary btn-sm">
                                                    Generate ERA
                                                </ButtonLoader>
                                            </Maybe>
                                        </td>
                                    </tr>
                                )}

                            </Table.Body>
                        </Table>

                        <Maybe condition={employeesLoading === false && employees?.employees?.docs?.length > 0}>
                            <div className="mt-6">
                                <Pagination data={employees?.employees} />
                            </div>
                        </Maybe>

                    </div>
                </div>

            </div>
        </div>
    );
}


export default List;